import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { TextLink } from "../components/kit/TextLink";
import { ArrowLeft, Eye, Trophy } from "lucide-react";
import api from "../lib/api";
import { getWithRetry } from "../lib/fetchWithRetry";
import { toStatsQuery } from "../lib/statsQuery";
import { dateTimeLabel } from "../lib/clockTime";
import { Card, CardContent } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import CareerStatSections from "../components/CareerStatSections";
import LoadFailureNote from "../components/LoadFailureNote";
import { coachPlayersName } from "../lib/coachPlayersName";
import { useAuth } from "../context/AuthContext";
import { newUi } from "../lib/featureFlags";
import CoachRecordScreen from "../components/CoachRecordScreen";

// #1289 (D298, D217, D228.2): a coach with the coach-player look on gets the
// rebuilt full record; an admin and the flag-off leg keep the shipped page below.
export default function CoachPlayerRecord() {
  const { user } = useAuth();
  if (newUi("coach-player") && user?.role === "coach") return <CoachRecordScreen />;
  return <ShippedCoachPlayerRecord />;
}

/**
 * Issue #719 half 2 — the coach's read-only view of a linked player's
 * record.
 *
 * WHAT WAS MISSING, precisely. Every screen that lists a player's MATCHES
 * belongs to the player: `/dashboard`, `/statistics` and `/performance` are
 * all player-role routes (the third one only since this issue — see
 * App.js). The coach portal shows a roster, a health record, goals,
 * feedback, guides, reflections and the aggregate Career snapshot (#460) —
 * and nowhere among them the per-match log. So a coach could read a
 * player's career average and not the four innings it came from, which is
 * the thing you actually look at before a Friday.
 *
 * NO NEW API, AND THAT IS THE POINT. `/stats/summary?player_id=` and
 * `/matches?player_id=` have taken that param through `resolveScope`
 * (api/src/routes/authz.ts) since #25: a coach is admitted only by an
 * ACTIVE `coach_links` row, and an absent/`invited`/`revoked` link, a
 * player id that is not a player, and a player id that does not exist all
 * resolve to the same generic 404 — the anti-enumeration rule that module
 * exists to enforce. A new `/coach/players/:id/record` endpoint would have
 * been a second copy of that gate to keep in step with the first, for no
 * capability the two existing reads do not already have. What was missing
 * was never the authorization; it was a way in. `api/test/coachPlayerRecord.test.ts`
 * pins the matrix behind both reads so this page's guarantee is asserted
 * rather than assumed.
 *
 * WHY `?player=` AND NOT `/coach-portal/players/:playerId`. Two reasons,
 * and the second is the deciding one. `?player=<id>` is already this app's
 * way of naming a player on a coach surface — the coach Dashboard's roster
 * cards (#74) and the notifications bell (#383/#464) both link to
 * `/coach-portal?player=…`, and CoachPortal.jsx reads it on mount and on
 * same-route navigation. And `e2e/route-params.js` HARD-FAILS on a
 * parameterised route with no fixed URL to visit, by design; a player
 * profile id is a ULID minted at seed time, so there is no fixed URL to
 * give it. A static path keeps this page in the responsive matrix, where a
 * `:playerId` route would have had to be excluded from it.
 *
 * READ-ONLY, AND STRUCTURALLY SO. There is no form, no dialog, no write
 * call and no mutation import in this file. A coach's writing surfaces —
 * notes, goals, coach reflections, guide assignment — all live on the
 * portal and are unchanged; this page exists to be read from.
 *
 * WHAT A COACH SEES HERE IS THE COACH LINK'S SCOPE, NOT A SHARING CHOICE.
 * `identity_sharing` (#292) is per section per audience and covers the
 * player's Identity & Vision — their own words — and nothing on this page
 * is one of those sections. A player's match record has no sharing switch
 * of its own in the schema: granting a coach an active link is what grants
 * it, and revoking the link is what takes it away. That is stated here so
 * the next reader does not go looking for a per-section filter that is not
 * missing but absent by design.
 */
function ShippedCoachPlayerRecord() {
  const [sp] = useSearchParams();
  const playerId = sp.get("player");

  // The roster, not a per-player profile read: `GET /players` already
  // returns exactly this coach's actively-linked players
  // (api/src/routes/players.ts), so finding the row here is the same
  // active-link answer the two data reads below get from `resolveScope` —
  // and asking for a roster the caller is entitled to in full reveals
  // nothing about anyone outside it.
  const [player, setPlayer] = useState(null);
  const [rosterState, setRosterState] = useState("loading"); // loading | found | absent | failed
  const [summary, setSummary] = useState(null);
  const [summaryFailed, setSummaryFailed] = useState(false);
  const [matches, setMatches] = useState([]);
  const [matchesFailed, setMatchesFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!playerId) return;
    let cancelled = false;
    setRosterState("loading");
    api
      .get("/players")
      .then((r) => {
        if (cancelled) return;
        const row = (r.data || []).find((p) => p.player_id === playerId);
        setPlayer(row || null);
        setRosterState(row ? "found" : "absent");
      })
      .catch((e) => {
        if (cancelled) return;
        // #271: "we could not reach the roster" is not "this player is not
        // yours". Kept apart so the page never accuses a link of being
        // gone because a request failed.
        setRosterState("failed");
        console.error("CoachPlayerRecord: failed to load /players", e);
      });
    return () => { cancelled = true; };
  }, [playerId, reloadKey]);

  // BOTH data effects wait for the roster to answer, and that ordering is
  // the point rather than an optimisation (#720 review, m1). A hand-typed
  // `?player=<a-stranger>` is a DENIAL, and the page has already decided to
  // say so by the time these would fire — sending the two requests anyway
  // collects two correct 404s and writes two `console.error`s for them, so
  // the only signal a support person has starts carrying entries for the
  // app working exactly as designed. A denial is not a failure. The cost is
  // one round trip on the happy path, against a roster read the coach's
  // portal makes anyway.
  useEffect(() => {
    if (!playerId || rosterState !== "found") return;
    let cancelled = false;
    setSummaryFailed(false);
    getWithRetry(`/stats/summary${toStatsQuery({ playerId })}`)
      .then((r) => { if (!cancelled) setSummary(r.data); })
      .catch((e) => {
        if (cancelled) return;
        setSummary(null);
        setSummaryFailed(true);
        console.error("CoachPlayerRecord: failed to load /stats/summary", e);
      });
    return () => { cancelled = true; };
  }, [playerId, rosterState, reloadKey]);

  useEffect(() => {
    if (!playerId || rosterState !== "found") return;
    let cancelled = false;
    setMatchesFailed(false);
    getWithRetry(`/matches?player_id=${encodeURIComponent(playerId)}`)
      .then((r) => { if (!cancelled) setMatches(r.data || []); })
      .catch((e) => {
        if (cancelled) return;
        // The same #271 posture PerformanceTracking.jsx takes on this exact
        // request: an empty list after a failure would tell a coach the
        // player has logged nothing, which is the one thing this page must
        // never say without knowing it.
        setMatches([]);
        setMatchesFailed(true);
        console.error("CoachPlayerRecord: failed to load /matches", e);
      });
    return () => { cancelled = true; };
  }, [playerId, rosterState, reloadKey]);

  const backLink = (
    <TextLink
      as={Link}
      to={playerId ? `/coach-portal?player=${encodeURIComponent(playerId)}` : "/coach-portal"}
      className="gap-1.5 text-sm font-semibold text-[#0B2E59]"
      data-testid="cpr-back"
    >
      <ArrowLeft size={15} /> {coachPlayersName()}
    </TextLink>
  );

  if (!playerId) {
    return (
      <div className="space-y-4" data-testid="coach-player-record">
        {backLink}
        <Card className="border-slate-200">
          <CardContent className="p-6 text-sm text-slate-600" data-testid="cpr-no-player">
            Choose a player on {coachPlayersName()}, then open their record from the roster row.
          </CardContent>
        </Card>
      </div>
    );
  }

  // "Not on your roster" and "not a player at all" are deliberately the
  // same sentence, for the reason `routes/authz.ts` gives the same 404 to
  // both: a different message for each would answer "does this id exist?"
  // for a coach who was never entitled to ask.
  if (rosterState === "absent") {
    return (
      <div className="space-y-4" data-testid="coach-player-record">
        {backLink}
        <Card className="border-slate-200">
          <CardContent className="p-6 text-sm text-slate-600" data-testid="cpr-not-found">
            There is no record here for you to read. A player's record opens once they have an active
            link with you — ask them to share, or pick someone from {coachPlayersName()}.
          </CardContent>
        </Card>
      </div>
    );
  }

  // Held until the roster answers. Rendering the page shell first would put
  // "You are reading this player's record" on screen for a player this
  // coach may turn out not to be linked to — a sentence that is wrong in
  // exactly the direction this page must never be wrong in.
  if (rosterState === "loading") {
    return (
      <div className="space-y-4" data-testid="coach-player-record">
        {backLink}
        <Card className="border-slate-200">
          <CardContent className="p-6 text-sm text-slate-500" data-testid="cpr-loading">Loading…</CardContent>
        </Card>
      </div>
    );
  }

  if (rosterState === "failed") {
    return (
      <div className="space-y-4" data-testid="coach-player-record">
        {backLink}
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <LoadFailureNote
              what="this player's record"
              testId="cpr-roster-error"
              onRetry={() => setReloadKey((k) => k + 1)}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  const playerName = player?.full_name || "this player";

  return (
    <div className="space-y-6 min-w-0" data-testid="coach-player-record">
      {backLink}

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.3em] text-slate-500">Read-only</p>
          <h1
            className="font-display font-black text-3xl sm:text-4xl text-slate-900 mt-1 break-words"
            data-testid="cpr-heading"
          >
            {playerName}'s record
          </h1>
        </div>
      </div>

      {/* The one sentence that has to be on this page. A coach reading a
          record needs to know at a glance that it is somebody else's and
          that nothing they do here changes it. */}
      <div
        className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 min-w-0"
        data-testid="cpr-reading-notice"
      >
        <Eye size={16} className="mt-0.5 shrink-0 text-slate-500" />
        <p className="min-w-0">
          You are reading {playerName}'s record. It is theirs to change — nothing on this page edits it.
        </p>
      </div>

      <Card className="border-slate-200 min-w-0">
        <CardContent className="p-6 space-y-6 min-w-0">
          <div className="flex items-center gap-2">
            <Trophy size={16} className="text-[#D4A017]" />
            <p className="text-sm font-semibold text-slate-900">Career</p>
          </div>
          {summaryFailed ? (
            <LoadFailureNote
              what="this player's career figures"
              testId="cpr-stats-error"
              onRetry={() => setReloadKey((k) => k + 1)}
            />
          ) : !summary ? (
            <p className="text-slate-500 text-sm" data-testid="cpr-stats-loading">Loading…</p>
          ) : (
            <CareerStatSections d={summary} />
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200 min-w-0">
        <CardContent className="p-6 space-y-4 min-w-0">
          <p className="text-sm font-semibold text-slate-900">Matches</p>
          {matchesFailed ? (
            <LoadFailureNote
              what="this player's matches"
              testId="cpr-matches-error"
              onRetry={() => setReloadKey((k) => k + 1)}
            />
          ) : matches.length === 0 ? (
            <p className="text-slate-500 text-sm" data-testid="cpr-matches-empty">
              {playerName} has not logged a match yet.
            </p>
          ) : (
            <MatchRecordList matches={matches} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * The match log, read-only.
 *
 * Deliberately NOT an extraction of `PerformanceTracking.jsx`'s table. That
 * one is the player's own editing surface: every row carries an expand
 * toggle, an edit button, a delete confirm and a reflection prompt, and its
 * columns are wired to a persisted sort and a search box. Lifting it whole
 * would have meant threading "is this read-only?" through all of that, and
 * a shared component whose main job is to hide half of itself is how an
 * edit control ends up on a coach's screen after the next change to it.
 * The numbers are not forked — they are the `toApiMatch` fields as sent
 * (api/src/routes/matches.ts); the presentation is.
 *
 * `overflow-x-auto` on the table wrapper and a card list below the `md`
 * breakpoint, matching the pattern the responsive harness already holds
 * every other wide table in this app to.
 */
function MatchRecordList({ matches }) {
  const rows = [...matches].sort((a, b) => String(b.match_date).localeCompare(String(a.match_date)));
  const num = (v) => (v === null || v === undefined ? "—" : v);
  // #715: the not-out star belongs to an innings that happened. `not_out`
  // is stored true for a match with no batting (the form defaults the
  // dismissal to "Not Out"), so without the runs guard a match nobody
  // batted in reads `—*`. Same guard as PerformanceTracking.jsx.
  const star = (m) => (m.batting?.runs != null && m.batting?.not_out ? "*" : "");

  return (
    <>
      <div className="hidden md:block overflow-x-auto min-w-0">
        <Table data-testid="cpr-matches-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Date</TableHead>
              <TableHead>Competition</TableHead>
              <TableHead>Opponent</TableHead>
              <TableHead className="text-right">Runs</TableHead>
              <TableHead className="text-right">Balls</TableHead>
              <TableHead className="text-right">SR</TableHead>
              <TableHead>Dismissal</TableHead>
              <TableHead className="text-right">Overs</TableHead>
              <TableHead className="text-right">Wkts</TableHead>
              <TableHead className="text-right">Econ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((m) => (
              <TableRow key={m.match_id} data-testid={`cpr-match-${m.match_id}`}>
                <TableCell className="text-sm whitespace-nowrap">{dateTimeLabel(m.match_date, m.match_time)}</TableCell>
                <TableCell className="text-xs text-slate-600">{m.competition || "—"}</TableCell>
                <TableCell className="font-semibold text-slate-900">{m.opponent}</TableCell>
                <TableCell className="text-right font-semibold text-[#0B2E59]">
                  {num(m.batting?.runs)}{star(m)}
                </TableCell>
                <TableCell className="text-right">{num(m.batting?.balls_faced)}</TableCell>
                <TableCell className="text-right">{num(m.batting?.strike_rate)}</TableCell>
                <TableCell className="text-xs text-slate-500">{m.batting?.dismissal_type || "—"}</TableCell>
                <TableCell className="text-right">{num(m.bowling?.overs)}</TableCell>
                <TableCell className="text-right font-semibold text-[#D4A017]">{num(m.bowling?.wickets)}</TableCell>
                <TableCell className="text-right">{num(m.bowling?.economy_rate)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="md:hidden space-y-3" data-testid="cpr-matches-cards">
        {rows.map((m) => (
          <div key={m.match_id} className="rounded-lg border border-slate-200 p-3 min-w-0" data-testid={`cpr-match-card-${m.match_id}`}>
            <div className="flex items-baseline justify-between gap-2 min-w-0">
              <p className="font-semibold text-slate-900 truncate">{m.opponent}</p>
              <p className="text-xs text-slate-500 whitespace-nowrap">{dateTimeLabel(m.match_date, m.match_time)}</p>
            </div>
            <p className="text-xs text-slate-500 mt-0.5 truncate">{m.competition || "—"}</p>
            <div className="grid grid-cols-4 gap-2 mt-3">
              <Cell label="Runs" value={`${num(m.batting?.runs)}${star(m)}`} />
              <Cell label="Balls" value={num(m.batting?.balls_faced)} />
              <Cell label="Wkts" value={num(m.bowling?.wickets)} />
              <Cell label="Econ" value={num(m.bowling?.economy_rate)} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function Cell({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-slate-400">{label}</p>
      <p className="text-sm font-semibold text-slate-900 truncate">{value}</p>
    </div>
  );
}
